import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { AiIcon } from "./AiIcon";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/patterns";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { aiFeedback, useAiFeature } from "@/lib/ai";
import { AiButton } from "./AiButton";

interface Change {
  field: string;
  current: unknown;
  suggested: unknown;
}
interface Reading {
  suggestion_id: string;
  summary: string;
  urgency: "HOT" | "WARM" | "COLD";
  urgency_reason: string;
  questions_to_ask: string[];
  changes: Change[];
}

const LABEL: Record<string, string> = {
  product_interest: "Product interest",
  expected_budget: "Budget",
  expected_timeline: "Timeline",
  city: "City",
  state: "State",
  industry: "Industry",
  priority: "Priority",
};
const TONE = { HOT: "danger", WARM: "warning", COLD: "neutral" } as const;
const show = (field: string, v: unknown) => (v === null || v === undefined || v === "" ? "—" : field === "expected_budget" ? `₹${Number(v).toLocaleString("en-IN")}` : field === "priority" ? String(v).charAt(0) + String(v).slice(1).toLowerCase() : String(v));

/**
 * What the AI read in a lead's enquiry: a short summary, how urgent it looks, questions for the first call, and the fields
 * it would fill. Nothing changes on the lead until the rep ticks the fields and applies them.
 */
export function LeadEnquiryCard({ leadId, canUpdate, hasEnquiry, onApplied }: { leadId: string; canUpdate: boolean; hasEnquiry: boolean; onApplied: () => void }) {
  const available = useAiFeature("LEAD_ENQUIRY");
  const { success, error } = useToast();
  const [reading, setReading] = useState<Reading | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const take = (r: Reading | null) => {
    setReading(r && r.suggestion_id ? r : null);
    setPicked(new Set((r?.changes || []).map((c) => c.field)));
  };

  useEffect(() => {
    if (!available) return;
    api
      .get(`/api/Ai/LeadEnquiry/${leadId}`)
      .then((r) => take(unwrapObject<Reading | null>(r, null)))
      .catch(() => undefined);
  }, [available, leadId]);

  if (!available) return null;

  const read = async () => {
    setBusy(true);
    try {
      take(unwrapObject<Reading>(await api.post(`/api/Ai/LeadEnquiry/${leadId}`)));
    } catch (e: any) {
      error("Couldn't read the enquiry", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!reading) return;
    setBusy(true);
    const chosen = reading.changes.filter((c) => picked.has(c.field));
    const failed: string[] = [];
    for (const c of chosen) {
      try {
        await api.patch(`/api/InlineEdit/Lead/${leadId}`, { field: c.field, value: c.suggested, previous: c.current ?? null });
      } catch (e: any) {
        failed.push(`${LABEL[c.field] || c.field}: ${e.message}`);
      }
    }
    aiFeedback(reading.suggestion_id, true, chosen.length !== reading.changes.length);
    setBusy(false);
    if (failed.length) error("Some fields weren't changed", failed.join("; "));
    else success("Applied", `${chosen.length} field${chosen.length === 1 ? "" : "s"} updated from the enquiry`);
    setReading(null);
    onApplied();
  };

  const dismiss = () => {
    if (reading) aiFeedback(reading.suggestion_id, false);
    setReading(null);
  };

  if (!reading) {
    return hasEnquiry ? (
      <AiButton busy={busy} onClick={read} title="Read the enquiry and suggest product, budget, timeline and urgency">
        Read the enquiry with AI
      </AiButton>
    ) : null;
  }

  return (
    <section className="flex flex-col gap-2.5 rounded-lg border border-primary/25 bg-accent/40 px-3.5 py-3" aria-label="AI reading of the enquiry">
      <div className="flex items-start justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-accent-foreground">
          <AiIcon className="size-4" /> AI read the enquiry
        </h2>
        <button type="button" aria-label="Dismiss" title="Dismiss" onClick={dismiss} className="text-muted-foreground hover:text-foreground">
          <X className="size-4" />
        </button>
      </div>
      <p className="text-[13px]">{reading.summary}</p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <StatusPill tone={TONE[reading.urgency]}>{show("priority", reading.urgency)}</StatusPill>
        <span className="text-muted-foreground">{reading.urgency_reason}</span>
      </div>
      {reading.questions_to_ask.length > 0 && (
        <div>
          <div className="text-[11.5px] font-semibold text-muted-foreground">Ask on the first call</div>
          <ul className="ml-4 list-disc text-[13px]">
            {reading.questions_to_ask.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      )}
      {reading.changes.length > 0 && (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-[11.5px] font-semibold text-muted-foreground">Fill in on the lead</legend>
          {reading.changes.map((c) => (
            <label key={c.field} className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                className="mt-1"
                disabled={!canUpdate}
                checked={picked.has(c.field)}
                onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(c.field); else n.delete(c.field); return n; })}
              />
              <span>
                <span className="font-medium">{LABEL[c.field] || c.field}:</span> <span className="text-muted-foreground line-through decoration-muted-foreground/50">{c.current != null && c.current !== "" ? show(c.field, c.current) : ""}</span> {show(c.field, c.suggested)}
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="flex flex-wrap gap-2">
        {canUpdate && reading.changes.length > 0 && (
          <Button size="sm" disabled={busy || picked.size === 0} onClick={apply}>
            Apply {picked.size} selected
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={dismiss}>
          {reading.changes.length ? "Dismiss" : "Done"}
        </Button>
      </div>
    </section>
  );
}
